import { AxiosInstance } from 'axios';
import { Result } from 'services/Result';
import {
  ActiveSubscriptionsKpiResponse,
  CertificationCompletionResponse,
  CertificationUserCount,
  MonthlyCertRevenueBreakdown,
  MonthlyChurnRate,
  MonthlyDiscountedSalesItems,
  MonthlyRecurringRevenue,
  MonthlyRefundTotal,
  MonthlySubscriptionActivity,
  MonthlySubscriptionRevenue,
  OrganizationsResponse,
  OrganizationsSummaryResponse,
  WebinarsSummaryResponseEntry,
} from 'types/admin/analytics';

export class AnalyticsService {
  constructor(private axiosInstance: AxiosInstance) {}

  getCertificationUserCounts = async (): Promise<CertificationUserCount[]> => {
    const response = await this.axiosInstance.get<Result<CertificationUserCount[]>>('/api/admin/analytics/certifications/user-counts');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching certification user counts.');
  };

  getOrganizations = async (): Promise<OrganizationsResponse[]> => {
    const response = await this.axiosInstance.get<Result<OrganizationsResponse[]>>('/api/admin/analytics/organizations');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching organizations.');
  };

  getStudentsCount = async (): Promise<number> => {
    const response = await this.axiosInstance.get<Result<number>>('/api/admin/analytics/students-count');
    if (response.data && !response.data.isError && response.data.data !== undefined) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching students count.');
  };

  getOrganizationsSummary = async (): Promise<OrganizationsSummaryResponse[]> => {
    const response = await this.axiosInstance.get<Result<OrganizationsSummaryResponse[]>>('/api/admin/analytics/organizations');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching organizations summary.');
  };

  getWebinarsSummary = async (): Promise<WebinarsSummaryResponseEntry[]> => {
    const response = await this.axiosInstance.get<Result<WebinarsSummaryResponseEntry[]>>('/api/admin/analytics/webinars/snapshots');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching webinars summary.');
  };

  getActiveSubscriptionsKpi = async (): Promise<ActiveSubscriptionsKpiResponse> => {
    const response = await this.axiosInstance.get<Result<ActiveSubscriptionsKpiResponse>>('/api/admin/analytics/subscribers/active-kpi');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching active subscriptions KPI.');
  };

  getMonthlySubscriptionActivity = async (months = 6): Promise<MonthlySubscriptionActivity[]> => {
    const response = await this.axiosInstance.get<Result<MonthlySubscriptionActivity[]>>(
      '/api/admin/analytics/financial/subscription-activity',
      { params: { months } }
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching monthly subscription activity.');
  };

  getMonthlyRefunds = async (months = 6): Promise<MonthlyRefundTotal[]> => {
    const response = await this.axiosInstance.get<Result<MonthlyRefundTotal[]>>('/api/admin/analytics/financial/refunds', {
      params: { months },
    });
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching monthly refunds.');
  };

  getMonthlyDiscountedSalesItems = async (months = 6): Promise<MonthlyDiscountedSalesItems[]> => {
    const response = await this.axiosInstance.get<Result<MonthlyDiscountedSalesItems[]>>(
      '/api/admin/analytics/financial/discounted-sales-items',
      { params: { months } }
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching monthly discounted sales items.');
  };

  getMonthlyChurnRates = async (months = 6): Promise<MonthlyChurnRate[]> => {
    const response = await this.axiosInstance.get<Result<MonthlyChurnRate[]>>('/api/admin/analytics/financial/churn-rates', {
      params: { months },
    });
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching monthly churn rates.');
  };

  getCertRevenueBreakdown = async (months = 6): Promise<MonthlyCertRevenueBreakdown[]> => {
    const response = await this.axiosInstance.get<Result<MonthlyCertRevenueBreakdown[]>>(
      '/api/admin/analytics/financial/cert-revenue-breakdown',
      { params: { months } }
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching certification revenue breakdown.');
  };

  getMonthlyRecurringRevenue = async (months = 6): Promise<MonthlyRecurringRevenue[]> => {
    const response = await this.axiosInstance.get<Result<MonthlyRecurringRevenue[]>>(
      '/api/admin/analytics/financial/recurring-revenue',
      { params: { months } }
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching monthly recurring revenue.');
  };

  getMonthlySubscriptionRevenue = async (months = 6): Promise<MonthlySubscriptionRevenue[]> => {
    const response = await this.axiosInstance.get<Result<MonthlySubscriptionRevenue[]>>(
      '/api/admin/analytics/financial/subscription-revenue',
      { params: { months } }
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching monthly subscription revenue.');
  };

  getMasterCertifiedCount = async (): Promise<number> => {
    const response = await this.axiosInstance.get<Result<number>>('/api/admin/analytics/certifications/master-certified-count');
    if (response.data && !response.data.isError && response.data.data !== undefined) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching master certified count.');
  };

  getCertificationCompletion = async (): Promise<CertificationCompletionResponse> => {
    const response = await this.axiosInstance.get<Result<CertificationCompletionResponse>>(
      '/api/admin/analytics/certifications/completion'
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching certification completion.');
  };
}
